<template>
  <div class="agent-ia-tab">
    <!-- Loader inicial mientras consultamos al backend -->
    <LoadingSpinner v-if="loading" />

    <!-- Estado vacío: aún no hay agente configurado para este cliente -->
    <div v-else-if="!hasAgentConfig && !isCreating" class="card empty-state-card">
      <div class="card-body text-center py-5">
        <div class="empty-state-icon mb-3">
          <i class="fas fa-robot"></i>
        </div>
        <h5 class="fw-bold mb-2">
          {{ $t('lang.agent_ai_client.no_agent_configured_title') }}
        </h5>
        <p class="text-muted mb-4 mx-auto" style="max-width: 480px;">
          {{ $t('lang.agent_ai_client.no_agent_configured_description') }}
        </p>
        <button
          type="button"
          class="btn btn-primary"
          @click="startConfiguration"
        >
          <i class="fas fa-plus-circle me-2"></i>
          {{ $t('lang.agent_ai_client.start_configuration') }}
        </button>
      </div>
    </div>

    <template v-else>
    <!-- Aviso si estamos creando por primera vez -->
    <div v-if="isCreating" class="alert alert-info d-flex align-items-center gap-2 mb-3" role="alert">
      <i class="fas fa-info-circle"></i>
      <div class="flex-grow-1">
        {{ $t('lang.agent_ai_client.creation_mode_notice') }}
      </div>
      <button
        type="button"
        class="btn btn-sm btn-link text-decoration-none p-0"
        @click="exitCreation"
      >
        {{ $t('lang.button.cancel') }}
      </button>
    </div>

    <!-- ID del agente en ElevenLabs -->
    <div class="card status-card mb-3">
      <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="flex-grow-1 me-2">
          <h6 class="fw-bold mb-1">{{ $t('lang.agent_ai_client.elevenlabs_agent_id_label') }}</h6>
          <p class="text-muted small mb-0">
            {{ $t('lang.agent_ai_client.elevenlabs_agent_id_description') }}
          </p>
        </div>
        <div class="agent-id-control d-flex align-items-center gap-2">
          <input
            ref="agentIdInput"
            v-model.trim="elevenlabsAgentId"
            type="text"
            class="form-control form-control-sm agent-id-input"
            :disabled="isAgentIdInputDisabled"
            :placeholder="$t('lang.agent_ai_client.elevenlabs_agent_id_placeholder')"
            spellcheck="false"
            autocomplete="off"
          />
          <button
            v-if="!isCreating"
            type="button"
            class="btn btn-sm btn-outline-secondary agent-id-lock-btn"
            :class="{ 'is-locked': isAgentIdLocked, 'is-unlocked': !isAgentIdLocked }"
            :title="isAgentIdLocked
              ? $t('lang.agent_ai_client.elevenlabs_agent_id_unlock_tooltip')
              : $t('lang.agent_ai_client.elevenlabs_agent_id_lock_tooltip')"
            @click="toggleAgentIdLock"
          >
            <i :class="isAgentIdLocked ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Teléfono asociado al agente en ElevenLabs -->
    <div class="card status-card mb-3">
      <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="flex-grow-1 me-2">
          <h6 class="fw-bold mb-1">{{ $t('lang.agent_ai_client.elevenlabs_phone_label') }}</h6>
          <p class="text-muted small mb-0">
            {{ $t('lang.agent_ai_client.elevenlabs_phone_description') }}
          </p>
          <div
            v-if="!hasElevenlabsPhone"
            class="alert alert-warning d-flex align-items-center gap-2 mt-2 mb-0 py-2 px-3"
            role="alert"
          >
            <i class="fas fa-exclamation-triangle"></i>
            <span class="small">
              {{ $t('lang.agent_ai_client.elevenlabs_phone_not_configured') }}
            </span>
          </div>
        </div>
        <div class="agent-id-control d-flex align-items-center gap-2">
          <input
            :value="elevenlabsPhoneNumber"
            type="text"
            class="form-control form-control-sm agent-id-input"
            :placeholder="$t('lang.agent_ai_client.elevenlabs_phone_placeholder')"
            spellcheck="false"
            autocomplete="off"
            readonly
            disabled
          />
        </div>
      </div>
    </div>


    <!-- Estado del servicio (activo / inactivo) -->
    <div class="card status-card mb-3">
      <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h6 class="fw-bold mb-1">{{ $t('lang.agent_ai_client.service_status_label') }}</h6>
          <p class="text-muted small mb-0">
            {{ $t('lang.agent_ai_client.service_status_description') }}
          </p>
        </div>
        <select
          v-model="isEnabled"
          class="form-select form-select-sm status-select"
        >
          <option :value="true">{{ $t('lang.agent_ai_client.option_yes') }}</option>
          <option :value="false">{{ $t('lang.agent_ai_client.option_no') }}</option>
        </select>
      </div>
    </div>

    <!-- Llamadas simultáneas máximas -->
    <div class="card status-card mb-3">
      <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h6 class="fw-bold mb-1">{{ $t('lang.agent_ai_client.max_concurrent_calls_label') }}</h6>
          <p class="text-muted small mb-0">
            {{ $t('lang.agent_ai_client.max_concurrent_calls_description') }}
          </p>
        </div>
        <input
          v-model.number="maxConcurrentCalls"
          type="number"
          min="1"
          :max="MAX_CONCURRENT_CALLS_LIMIT"
          step="1"
          class="form-control form-control-sm status-select"
          @blur="clampMaxConcurrentCalls"
        />
      </div>
    </div>

    <!-- Bloc des variables disponibles -->
    <div class="card variables-card mb-3">
      <div class="card-body">
        <h6 class="fw-bold mb-1">{{ $t('lang.agent_ai_client.available_variables') }}</h6>
        <p class="text-muted small mb-3">
          {{ $t('lang.agent_ai_client.variables_description') }}
        </p>

        <div
          v-for="category in variableCategories"
          :key="category.labelKey"
          class="variable-category mb-2"
        >
          <div class="variable-category-label">{{ category.label }}</div>
          <div class="variables-grid">
            <button
              v-for="v in category.variables"
              :key="v"
              type="button"
              class="variable-pill"
              :class="{ flash: flashedVar === v }"
              @click="insertVariable(v)"
            >
              <span v-text="'{' + '{' + v + '}' + '}'"></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Generación automática del prompt vía IA -->
    <div class="card status-card mb-3">
      <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="flex-grow-1 me-2">
          <h6 class="fw-bold mb-1">{{ $t('lang.agent_ai_client.generate_prompt_label') }}</h6>
          <p class="text-muted small mb-0">
            {{ $t('lang.agent_ai_client.generate_prompt_description') }}
          </p>
        </div>
        <button
          type="button"
          class="btn btn-primary btn-sm generate-prompt-btn"
          :disabled="generatingPrompt"
          @click="generatePromptWithAI"
        >
          <span v-if="generatingPrompt">
            <span class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('lang.agent_ai_client.generate_prompt_processing') }}
          </span>
          <span v-else>
            <i class="fas fa-magic me-1"></i>
            {{ $t('lang.agent_ai_client.generate_prompt_button') }}
          </span>
        </button>
      </div>
    </div>

    <!-- Champ prompt principal -->
    <div class="card prompt-card mb-3">
      <div class="card-body">
        <textarea
          ref="promptArea"
          v-model="prompt"
          class="form-control prompt-textarea"
          :placeholder="$t('lang.agent_ai_client.prompt_placeholder')"
        ></textarea>

        <div class="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
          <div class="small text-muted">
            <strong>{{ characterCount.toLocaleString(numberLocale) }}</strong>
            {{ $t('lang.agent_ai_client.characters_label') }}
            · ~<strong>{{ tokenCount.toLocaleString(numberLocale) }}</strong>
            {{ $t('lang.agent_ai_client.tokens_label') }}
          </div>
          <div v-if="hasChanges" class="small text-warning fw-bold">
            {{ $t('lang.agent_ai_client.unsaved_changes_warning') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Barre d'enregistrement -->
    <div class="card save-bar">
      <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2" :class="{ 'text-warning fw-bold': hasChanges }">
          <span class="status-dot" :class="{ modified: hasChanges }"></span>
          <span v-if="isCreating">{{ $t('lang.agent_ai_client.creation_mode_status') }}</span>
          <span v-else-if="hasChanges">{{ $t('lang.agent_ai_client.unsaved_changes') }}</span>
          <span v-else class="text-muted">{{ $t('lang.agent_ai_client.status_up_to_date') }}</span>
        </div>
        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="!canCancel || saving"
            @click="cancelChanges"
          >
            {{ $t('lang.button.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="!canSave"
            @click="savePrompt"
          >
            <span v-if="saving">
              <span class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('lang.button.saving') }}
            </span>
            <span v-else-if="isCreating">{{ $t('lang.agent_ai_client.create_agent_button') }}</span>
            <span v-else>{{ $t('lang.agent_ai_client.save_and_send_elevenlabs') }}</span>
          </button>
        </div>
      </div>
    </div>
    </template>

    <!-- Toast de feedback -->
    <div class="toast-feedback" :class="{ show: toastVisible }">
      {{ toastMessage }}
    </div>

    <!-- Modal de búsqueda del agente en ElevenLabs -->
    <modal
      name="elevenLabsAgentLookupModal"
      :resizable="false"
      :scrollable="false"
      :max-width="560"
      height="auto"
      width="50%"
      :click-to-close="!agentLookupLoading"
      style="z-index: 10"
      class="modal-preview"
      @opened="onAgentLookupModalOpened"
      @closed="onAgentLookupModalClosed"
    >
      <div class="p-4 agent-lookup-modal">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <div class="me-3">
            <h5 class="modal-title fw-bold mb-1">
              {{ $t('lang.agent_ai_client.agent_lookup_title') }}
            </h5>
            <p class="text-muted small mb-0">
              {{ $t('lang.agent_ai_client.agent_lookup_description') }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close-x"
            :disabled="agentLookupLoading"
            :aria-label="$t('lang.button.cancel')"
            @click="closeAgentLookupModal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <label class="form-label small fw-bold mb-1">
          {{ $t('lang.agent_ai_client.elevenlabs_agent_id_label') }}
        </label>
        <div class="d-flex gap-2 align-items-stretch">
          <input
            ref="agentLookupInputRef"
            v-model.trim="agentLookupAgentId"
            type="text"
            class="form-control form-control-sm agent-id-input flex-grow-1"
            :placeholder="$t('lang.agent_ai_client.elevenlabs_agent_id_placeholder')"
            :disabled="agentLookupLoading"
            spellcheck="false"
            autocomplete="off"
            @keydown.enter.prevent="searchAgentLookup"
          />
          <button
            type="button"
            class="btn btn-primary btn-sm agent-lookup-search-btn"
            :disabled="!canSearchAgent"
            @click="searchAgentLookup"
          >
            <span v-if="agentLookupLoading">
              <span class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('lang.agent_ai_client.agent_lookup_searching') }}
            </span>
            <span v-else>
              <i class="fas fa-search me-1"></i>
              {{ $t('lang.agent_ai_client.agent_lookup_search_button') }}
            </span>
          </button>
        </div>

        <div
          v-if="agentLookupError"
          class="alert alert-danger d-flex align-items-start gap-2 mt-3 mb-0 py-2 px-3"
          role="alert"
        >
          <i class="fas fa-exclamation-triangle mt-1"></i>
          <span class="small">{{ agentLookupError }}</span>
        </div>

        <div
          v-if="agentLookupSuccessMessage"
          class="alert alert-success d-flex align-items-start gap-2 mt-3 mb-0 py-2 px-3"
          role="status"
        >
          <i class="fas fa-check-circle mt-1"></i>
          <span class="small">{{ agentLookupSuccessMessage }}</span>
        </div>

        <div class="d-flex justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="agentLookupLoading"
            @click="closeAgentLookupModal"
          >
            {{ $t('lang.button.cancel') }}
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { agentsService } from '@/_services'
import LoadingSpinner from "@/components/LoadingSpinner.vue";

const MAX_CONCURRENT_CALLS_LIMIT = 4
const DEFAULT_MAX_CONCURRENT_CALLS = 1

/** Ordre aligné sur `agents.service.js` (blocs ElevenLabs / Oliverlist). */
const AGENT_PROMPT_BLOCK_KEYS = [
  'identity',
  'style',
  'objective',
  'product',
  'step1',
  'step2',
  'step3',
  'step4',
  'step5',
  'objections',
  'rules',
]

const VARIABLE_CATEGORY_DEFS = [
  {
    labelKey: 'lang.agent_ai_client.cat_prospect',
    variables: [
      'prospect_first_name',
      'prospect_last_name',
      'prospect_full_name',
      'prospect_email',
      'prospect_phone',
      'prospect_title',
      'prospect_company',
      'prospect_industry',
    ],
  },
  {
    labelKey: 'lang.agent_ai_client.cat_order_client',
    variables: ['order_context', 'order_id', 'client_id', 'client_name', 'comercial_user_first_name', 'comercial_user_last_name', 'comercial_user_id', 'email_step_1', 'email_step_2', 'email_step_3'],
  },
  {
    labelKey: 'lang.agent_ai_client.cat_call_history',
    variables: [
      'call_attempt_number',
      'previous_call_outcome',
      'previous_call_date',
      'email_history_summary',
    ],
  },
  {
    labelKey: 'lang.agent_ai_client.cat_datetime',
    variables: ['current_date', 'current_time', 'current_day_of_week'],
  },
  {
    labelKey: 'lang.agent_ai_client.cat_general_context',
    variables: ['cold_call_rules'],
  },
]

function serializePromptToEditorText(prompt) {
  if (prompt == null) return ''
  if (typeof prompt === 'string') return prompt
  if (typeof prompt !== 'object') return String(prompt)
  return AGENT_PROMPT_BLOCK_KEYS.map((k) => `[[${k}]]\n${String(prompt[k] != null ? prompt[k] : '')}`).join('\n\n')
}

function parseEditorTextToPrompt(text) {
  const trimmed = text.trim()
  if (!trimmed.startsWith('[[')) {
    return trimmed
  }
  const out = {}
  const positions = AGENT_PROMPT_BLOCK_KEYS.map((k) => ({
    key: k,
    idx: text.indexOf(`[[${k}]]`),
  }))
    .filter((p) => p.idx !== -1)
    .sort((a, b) => a.idx - b.idx)

  for (let i = 0; i < positions.length; i++) {
    const { key, idx } = positions[i]
    const marker = `[[${key}]]`
    const start = idx + marker.length
    const end = i + 1 < positions.length ? positions[i + 1].idx : text.length
    out[key] = text.slice(start, end).replace(/^\s*\n/, '').trimEnd()
  }
  AGENT_PROMPT_BLOCK_KEYS.forEach((k) => {
    if (out[k] === undefined) out[k] = ''
  })
  return out
}

export default {
  name: 'AgentAIClient',

  props: {
    clientId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      prompt: '',
      savedPrompt: '',
      isEnabled: false,
      savedIsEnabled: false,
      maxConcurrentCalls: DEFAULT_MAX_CONCURRENT_CALLS,
      savedMaxConcurrentCalls: DEFAULT_MAX_CONCURRENT_CALLS,
      MAX_CONCURRENT_CALLS_LIMIT,
      elevenlabsAgentId: '',
      savedElevenlabsAgentId: '',
      isAgentIdLocked: true,
      elevenlabsPhoneNumber: '',
      elevenlabsPhoneId: '',
      savedElevenlabsPhoneId: '',
      saving: false,
      loading: true,
      hasAgentConfig: false,
      isCreating: false,
      flashedVar: null,
      toastVisible: false,
      toastMessage: '',
      toastTimer: null,
      agentLookupAgentId: '',
      agentLookupLoading: false,
      agentLookupError: '',
      agentLookupSuccessMessage: '',
      agentLookupSuccessTimer: null,
      generatingPrompt: false,
    }
  },

  computed: {
    numberLocale() {
      const map = { en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT', pt: 'pt-PT' }
      return map[this.$i18n.locale] || 'en-US'
    },
    variableCategories() {
      return VARIABLE_CATEGORY_DEFS.map((c) => ({
        labelKey: c.labelKey,
        label: this.$t(c.labelKey),
        variables: c.variables,
      }))
    },
    characterCount() {
      return this.prompt.length
    },
    tokenCount() {
      return Math.ceil(this.prompt.length / 4)
    },
    hasChanges() {
      return (
        this.prompt !== this.savedPrompt ||
        this.isEnabled !== this.savedIsEnabled ||
        this.maxConcurrentCalls !== this.savedMaxConcurrentCalls ||
        this.elevenlabsAgentId !== this.savedElevenlabsAgentId ||
        this.elevenlabsPhoneId !== this.savedElevenlabsPhoneId
      )
    },
    hasElevenlabsPhone() {
      return (
        typeof this.elevenlabsPhoneId === 'string' &&
        this.elevenlabsPhoneId.trim().length > 0
      )
    },
    canSave() {
      if (this.saving) return false
      if (this.isCreating) {
        // En modo creación, exigimos al menos el elevenlabs_agent_id (requisito del backend).
        return this.elevenlabsAgentId.trim().length > 0
      }
      return this.hasChanges
    },
    canCancel() {
      return this.isCreating || this.hasChanges
    },
    canSearchAgent() {
      return (
        !this.agentLookupLoading &&
        typeof this.agentLookupAgentId === 'string' &&
        this.agentLookupAgentId.trim().length > 0
      )
    },
    isAgentIdInputDisabled() {
      // En modo creación, el `agent_id` ya viene validado del modal de búsqueda,
      // así que no se debe poder editar desde este campo. Solo en modo edición
      // (agente ya existente) el usuario puede desbloquearlo con el candado.
      return this.isCreating || this.isAgentIdLocked
    },
  },

  watch: {
    clientId() {
      this.loadPrompt()
    },
  },

  components: {
    LoadingSpinner    
  },

  mounted() {
    this.loadPrompt()
  },

  beforeDestroy() {
    clearTimeout(this.toastTimer)
    clearTimeout(this.agentLookupSuccessTimer)
  },

  methods: {
    resetForm() {
      this.prompt = ''
      this.savedPrompt = ''
      this.isEnabled = false
      this.savedIsEnabled = false
      this.maxConcurrentCalls = DEFAULT_MAX_CONCURRENT_CALLS
      this.savedMaxConcurrentCalls = DEFAULT_MAX_CONCURRENT_CALLS
      this.elevenlabsAgentId = ''
      this.savedElevenlabsAgentId = ''
      this.isAgentIdLocked = true
      this.elevenlabsPhoneNumber = ''
      this.elevenlabsPhoneId = ''
      this.savedElevenlabsPhoneId = ''
    },

    applyAgentData(data) {
      const raw = data && data.prompt
      this.prompt = serializePromptToEditorText(raw)
      this.savedPrompt = this.prompt
      const rawEnabled = data && data.is_enabled
      this.isEnabled =
        typeof rawEnabled === 'string'
          ? rawEnabled.toLowerCase() === 'yes'
          : !!rawEnabled
      this.savedIsEnabled = this.isEnabled

      const rawMax = data && data.max_concurrent_calls
      const parsedMax = parseInt(rawMax, 10)
      this.maxConcurrentCalls = this.normalizeMaxConcurrentCalls(parsedMax)
      this.savedMaxConcurrentCalls = this.maxConcurrentCalls

      const rawAgentId = data && data.elevenlabs_agent_id
      this.elevenlabsAgentId = rawAgentId != null ? String(rawAgentId).trim() : ''
      this.savedElevenlabsAgentId = this.elevenlabsAgentId
      this.isAgentIdLocked = this.elevenlabsAgentId.length > 0

      // El teléfono se obtiene siempre desde ElevenLabs (`elevenlab_agent.phone_numbers[0]`).
      // Mostramos `phone_number` (legible para el usuario) y enviamos `phone_number_id`
      // al guardar para que el backend lo persista en el campo `elevenlab_phone_id`.
      const elevenlabAgent = data && data.elevenlab_agent
      const phoneNumbers =
        elevenlabAgent && Array.isArray(elevenlabAgent.phone_numbers)
          ? elevenlabAgent.phone_numbers
          : []
      const firstPhone = phoneNumbers.length > 0 ? phoneNumbers[0] : null
      const rawPhoneNumber = firstPhone && firstPhone.phone_number
      const rawPhoneId = firstPhone && firstPhone.phone_number_id
      this.elevenlabsPhoneNumber = rawPhoneNumber != null ? String(rawPhoneNumber).trim() : ''
      this.elevenlabsPhoneId = rawPhoneId != null ? String(rawPhoneId).trim() : ''
      // `savedElevenlabsPhoneId` se sincroniza con lo que el backend ya tiene guardado
      // en BD (campo `elevenlab_phone_id`), para detectar si hace falta reenviarlo.
      const rawStoredPhoneId = data && data.elevenlab_phone_id
      this.savedElevenlabsPhoneId = rawStoredPhoneId != null ? String(rawStoredPhoneId).trim() : ''
    },

    async loadPrompt() {
      this.loading = true
      this.isCreating = false
      try {
        const data = await agentsService.getAgentConfig(this.clientId)
        if (data == null) {
          this.hasAgentConfig = false
          this.resetForm()
        } else {
          this.hasAgentConfig = true
          this.applyAgentData(data)
        }
      } catch (error) {
        console.error('AgentAIClient loadPrompt:', error)
        if (error && error.status === 404) {
          this.hasAgentConfig = false
          this.resetForm()
        } else {
          this.$emit('error', this.$t('lang.agent_ai_client.error_load_prompt'))
        }
      } finally {
        this.loading = false
      }
    },

    startConfiguration() {
      // Antes de pasar al modo creación, exigimos que el agente exista en
      // ElevenLabs y que tenga un número de teléfono asignado. Abrimos un modal
      // para que el usuario introduzca el agent_id; lo buscamos contra
      // `admin/get-elevenlabs-agent/{agent_id}` y solo si todo está OK entramos
      // en el modo creación con los datos pre-rellenados.
      this.agentLookupAgentId = ''
      this.agentLookupError = ''
      this.agentLookupSuccessMessage = ''
      this.agentLookupLoading = false
      clearTimeout(this.agentLookupSuccessTimer)
      this.$modal.show('elevenLabsAgentLookupModal')
    },

    onAgentLookupModalOpened() {
      this.$nextTick(() => {
        const el = this.$refs.agentLookupInputRef
        if (el && typeof el.focus === 'function') el.focus()
      })
    },

    onAgentLookupModalClosed() {
      this.agentLookupAgentId = ''
      this.agentLookupError = ''
      this.agentLookupSuccessMessage = ''
      this.agentLookupLoading = false
      clearTimeout(this.agentLookupSuccessTimer)
    },

    closeAgentLookupModal() {
      if (this.agentLookupLoading) return
      this.$modal.hide('elevenLabsAgentLookupModal')
    },

    async searchAgentLookup() {
      if (!this.canSearchAgent) return
      const typedAgentId = this.agentLookupAgentId.trim()
      this.agentLookupError = ''
      this.agentLookupSuccessMessage = ''
      this.agentLookupLoading = true
      try {
        const agentInfo = await agentsService.getElevenLabsAgent(typedAgentId)
        if (agentInfo == null || typeof agentInfo !== 'object') {
          this.agentLookupError = this.$t('lang.agent_ai_client.agent_lookup_error_not_found')
          return
        }
        // El backend puede responder con un agente válido en ElevenLabs pero que
        // ya está asignado a otro cliente de Oliverlist. En ese caso no se
        // permite reutilizarlo: cortamos el flujo aquí con un mensaje claro.
        if (agentInfo.exists_for_another_client === true) {
          this.agentLookupError = this.$t(
            'lang.agent_ai_client.agent_lookup_error_already_assigned',
          )
          return
        }
        const responseAgentId =
          typeof agentInfo.agent_id === 'string' ? agentInfo.agent_id.trim() : ''
        if (responseAgentId.length === 0) {
          this.agentLookupError = this.$t('lang.agent_ai_client.agent_lookup_error_not_found')
          return
        }
        const phoneNumbers = Array.isArray(agentInfo.phone_numbers)
          ? agentInfo.phone_numbers
          : []
        const firstPhone = phoneNumbers.length > 0 ? phoneNumbers[0] : null
        const phoneNumber =
          firstPhone && typeof firstPhone.phone_number === 'string'
            ? firstPhone.phone_number.trim()
            : ''
        const phoneId =
          firstPhone && typeof firstPhone.phone_number_id === 'string'
            ? firstPhone.phone_number_id.trim()
            : ''
        if (phoneNumber.length === 0 || phoneId.length === 0) {
          this.agentLookupError = this.$t('lang.agent_ai_client.agent_lookup_error_missing_phone')
          return
        }
        this.agentLookupSuccessMessage = this.$t(
          'lang.agent_ai_client.agent_lookup_success',
          { phone: phoneNumber },
        )
        this.applyFoundAgent({
          agentId: responseAgentId,
          phoneNumber,
          phoneId,
        })
        clearTimeout(this.agentLookupSuccessTimer)
        this.agentLookupSuccessTimer = setTimeout(() => {
          this.$modal.hide('elevenLabsAgentLookupModal')
        }, 900)
      } catch (error) {
        console.error('AgentAIClient searchAgentLookup:', error)
        if (error && error.status === 404) {
          this.agentLookupError = this.$t('lang.agent_ai_client.agent_lookup_error_not_found')
        } else {
          this.agentLookupError = this.$t('lang.agent_ai_client.agent_lookup_error_generic')
        }
      } finally {
        this.agentLookupLoading = false
      }
    },

    applyFoundAgent({ agentId, phoneNumber, phoneId }) {
      this.resetForm()
      this.isCreating = true
      this.elevenlabsAgentId = agentId
      this.savedElevenlabsAgentId = ''
      this.isAgentIdLocked = true
      this.elevenlabsPhoneNumber = phoneNumber
      this.elevenlabsPhoneId = phoneId
      this.savedElevenlabsPhoneId = ''
    },

    exitCreation() {
      if (this.hasChanges) {
        if (!confirm(this.$t('lang.agent_ai_client.confirm_cancel_changes'))) return
      }
      this.isCreating = false
      this.resetForm()
    },

    async savePrompt() {
      if (!this.canSave) return
      this.clampMaxConcurrentCalls()
      const trimmedAgentId = this.elevenlabsAgentId.trim()
      if (this.isCreating && trimmedAgentId.length === 0) {
        this.$emit('error', this.$t('lang.agent_ai_client.error_missing_elevenlabs_agent_id'))
        return
      }
      const wasCreating = this.isCreating || !this.hasAgentConfig
      const trimmedPhoneId = (this.elevenlabsPhoneId || '').trim()
      this.saving = true
      try {
        const promptPayload = parseEditorTextToPrompt(this.prompt)
        const payload = {
          prompt: promptPayload,
          isEnabled: this.isEnabled,
          maxConcurrentCalls: this.maxConcurrentCalls,
          elevenlabsAgentId: trimmedAgentId,
          elevenlabPhoneId: trimmedPhoneId,
        }
        const result = wasCreating
          ? await agentsService.createAgentPrompt(this.clientId, payload)
          : await agentsService.updateAgentPrompt(this.clientId, payload)

        // Algunos backends devuelven 200 OK con un cuerpo que indica que la sincronización
        // con ElevenLabs falló. Lo tratamos explícitamente como error.
        if (
          result &&
          typeof result === 'object' &&
          (result.saved === false ||
            result.synced_to_elevenlabs === false ||
            result.elevenlabs_status != null ||
            result.elevenlabs_response != null)
        ) {
          throw { ...result, status: result.status || 200 }
        }

        this.savedPrompt = this.prompt
        this.savedIsEnabled = this.isEnabled
        this.savedMaxConcurrentCalls = this.maxConcurrentCalls
        this.savedElevenlabsAgentId = trimmedAgentId
        this.elevenlabsAgentId = trimmedAgentId
        this.savedElevenlabsPhoneId = trimmedPhoneId
        if (trimmedAgentId.length > 0) {
          this.isAgentIdLocked = true
        }
        this.hasAgentConfig = true
        this.isCreating = false
        this.showToast(
          wasCreating
            ? this.$t('lang.agent_ai_client.toast_created')
            : this.$t('lang.agent_ai_client.toast_saved'),
        )
        // Si quedaba un mensaje de error de un intento anterior, lo limpiamos:
        // ahora el guardado ha sido exitoso y la alerta ya no aplica.
        this.$emit('clear-error')
        this.$emit('saved')
      } catch (error) {
        //console.error('AgentAIClient savePrompt:', error)
        if (error && error.status === 409) {
          // El backend dice que ya existe: pasamos a flujo de edición recargando datos.
          this.$emit('error', this.$t('lang.agent_ai_client.error_agent_already_exists'))
          this.isCreating = false
          await this.loadPrompt()
        } else if (this.isElevenLabsError(error)) {
          this.$emit('error', this.buildElevenLabsErrorMessage(error))
        } else {
          const fallbackKey = wasCreating
            ? 'lang.agent_ai_client.error_create_prompt'
            : 'lang.agent_ai_client.error_save_prompt'
          this.$emit('error', this.$t(fallbackKey))
        }
      } finally {
        this.saving = false
      }
    },

    isElevenLabsError(error) {
      if (!error || typeof error !== 'object') return false
      if (error.elevenlabs_status != null) return true
      if (error.elevenlabs_response != null) return true
      if (error.synced_to_elevenlabs === false) return true
      const msg = typeof error.error === 'string' ? error.error.toLowerCase() : ''
      return msg.includes('elevenlabs')
    },

    buildElevenLabsErrorMessage(error) {
      const agentId =
        (error && error.elevenlabs_response && error.elevenlabs_response.error && error.elevenlabs_response.error.agent_id) ||
        this.elevenlabsAgentId.trim()
      return this.$t('lang.agent_ai_client.error_save_prompt_elevenlabs', {
        agent_id: agentId || '',
      })
    },

    cancelChanges() {
      if (this.isCreating) {
        this.exitCreation()
        return
      }
      if (confirm(this.$t('lang.agent_ai_client.confirm_cancel_changes'))) {
        this.prompt = this.savedPrompt
        this.isEnabled = this.savedIsEnabled
        this.maxConcurrentCalls = this.savedMaxConcurrentCalls
        this.elevenlabsAgentId = this.savedElevenlabsAgentId
        this.elevenlabsPhoneId = this.savedElevenlabsPhoneId
        this.isAgentIdLocked = this.savedElevenlabsAgentId.length > 0
      }
    },

    toggleAgentIdLock() {
      this.isAgentIdLocked = !this.isAgentIdLocked
      if (!this.isAgentIdLocked) {
        this.$nextTick(() => {
          const el = this.$refs.agentIdInput
          if (el && typeof el.focus === 'function') {
            el.focus()
            if (typeof el.select === 'function') el.select()
          }
        })
      }
    },

    normalizeMaxConcurrentCalls(value) {
      const n = Number(value)
      if (!Number.isFinite(n) || n < 1) return DEFAULT_MAX_CONCURRENT_CALLS
      if (n > MAX_CONCURRENT_CALLS_LIMIT) return MAX_CONCURRENT_CALLS_LIMIT
      return Math.floor(n)
    },

    clampMaxConcurrentCalls() {
      this.maxConcurrentCalls = this.normalizeMaxConcurrentCalls(this.maxConcurrentCalls)
    },

    insertVariable(varName) {
      const textarea = this.$refs.promptArea
      const token = '{{' + varName + '}}'
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const before = this.prompt.substring(0, start)
      const after = this.prompt.substring(end)

      this.prompt = before + token + after

      this.$nextTick(() => {
        textarea.focus()
        const newPos = start + token.length
        textarea.setSelectionRange(newPos, newPos)
      })

      this.flashedVar = varName
      setTimeout(() => {
        this.flashedVar = null
      }, 600)
      this.showToast(this.$t('lang.agent_ai_client.toast_inserted', { token }))
    },

    showToast(msg) {
      this.toastMessage = msg
      this.toastVisible = true
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false
      }, 1500)
    },

    async generatePromptWithAI() {
      if (this.generatingPrompt) return
      this.generatingPrompt = true
      try {
        const response = await agentsService.generateClientPrompt(this.clientId)
        // El backend devuelve un objeto con la forma:
        //   { client_id, client_name, client_website, proposed_prompt: "<string>" }
        // Soportamos también `prompt` por compatibilidad y, en último recurso,
        // que la respuesta sea directamente un string.
        let generated = null
        if (response && typeof response === 'object') {
          if (typeof response.proposed_prompt === 'string') {
            generated = response.proposed_prompt
          } else if (response.prompt !== undefined) {
            generated = response.prompt
          }
        } else if (typeof response === 'string') {
          generated = response
        }
        if (generated == null || (typeof generated === 'string' && generated.trim().length === 0)) {
          this.$emit('error', this.$t('lang.agent_ai_client.error_generate_prompt'))
          return
        }
        this.prompt = serializePromptToEditorText(generated)
        this.$emit('clear-error')
        await this.$swal({
          icon: 'success',
          title: this.$t('lang.agent_ai_client.generate_prompt_success_title'),
          text: this.$t('lang.agent_ai_client.generate_prompt_success_text'),
          confirmButtonText: this.$t('lang.taskGeneration.understood'),
        })
        this.$nextTick(() => {
          const area = this.$refs.promptArea
          if (area) {
            area.scrollIntoView({ behavior: 'smooth', block: 'center' })
            area.focus()
          }
        })
      } catch (error) {
        console.error('AgentAIClient generatePromptWithAI:', error)
        this.$emit('error', this.$t('lang.agent_ai_client.error_generate_prompt'))
      } finally {
        this.generatingPrompt = false
      }
    },
  },
}
</script>

<style scoped>
.agent-ia-tab {
  --ol-primary: #4b4be1;
  --ol-primary-dark: #3c3489;
  --ol-primary-soft: #eeeefc;
  --ol-valide: #10b981;
  --ol-absent: #f59e0b;
  --ol-neutral-200: #e5e7eb;
  --ol-neutral-500: #6b7280;
  --ol-neutral-700: #374151;
  --ol-neutral-900: #111827;

  position: relative;
}

.status-card,
.variables-card,
.prompt-card,
.save-bar,
.empty-state-card {
  border: 1px solid #f4f4f7;
  border-radius: 12px;
}

.empty-state-card {
  background: linear-gradient(180deg, #fafaff 0%, #ffffff 100%);
}

.empty-state-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--ol-primary-soft);
  color: var(--ol-primary);
  font-size: 1.75rem;
}

.status-select {
  width: auto;
  min-width: 110px;
}

.agent-id-control {
  min-width: 280px;
}

.agent-id-input {
  width: 260px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.8rem;
}

.agent-id-input:disabled {
  background-color: #f8f9fa;
  color: var(--ol-neutral-700);
  cursor: not-allowed;
}

.agent-id-lock-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 31px;
  padding: 0;
  flex-shrink: 0;
}

.agent-id-lock-btn.is-unlocked {
  color: var(--ol-primary);
  border-color: var(--ol-primary);
}

.agent-id-lock-btn.is-unlocked:hover {
  background: var(--ol-primary-soft);
  color: var(--ol-primary-dark);
}

.save-bar {
  position: sticky;
  bottom: 1rem;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.08);
  z-index: 10;
}

.variable-category-label {
  font-size: 0.7rem;
  color: var(--ol-neutral-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 0.4rem;
}

.variables-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.variable-pill {
  background: var(--ol-primary-soft);
  color: var(--ol-primary-dark);
  border: none;
  padding: 0.35rem 0.7rem;
  border-radius: 100px;
  font-size: 0.78rem;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  cursor: pointer;
  transition: all 0.15s;
  font-weight: 500;
}

.variable-pill:hover {
  background: var(--ol-primary);
  color: white;
  transform: translateY(-1px);
}

.variable-pill.flash {
  background: var(--ol-valide);
  color: white;
}

.prompt-textarea {
  min-height: 600px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.875rem;
  line-height: 1.65;
  color: var(--ol-neutral-900);
  border: 1px solid var(--ol-neutral-200);
  border-radius: 8px;
  resize: vertical;
}

.prompt-textarea:focus {
  border-color: var(--ol-primary);
  box-shadow: 0 0 0 3px var(--ol-primary-soft);
}

.btn-primary {
  background: var(--ol-primary);
  border-color: var(--ol-primary);
}
.btn-primary:hover:not(:disabled),
.btn-primary:focus:not(:disabled) {
  background: var(--ol-primary-dark);
  border-color: var(--ol-primary-dark);
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ol-valide);
}
.status-dot.modified {
  background: var(--ol-absent);
}

.toast-feedback {
  position: fixed;
  bottom: 5rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--ol-neutral-900);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-size: 0.85rem;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}
.toast-feedback.show {
  opacity: 1;
}

.agent-lookup-modal .btn-close-x {
  border: none;
  background: transparent;
  color: var(--ol-neutral-500);
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.agent-lookup-modal .btn-close-x:hover:not(:disabled) {
  background: var(--ol-neutral-200);
  color: var(--ol-neutral-900);
}

.agent-lookup-modal .btn-close-x:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.agent-lookup-modal .agent-lookup-search-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.generate-prompt-btn {
  flex-shrink: 0;
  white-space: nowrap;
  min-width: 180px;
}
</style>
